import React from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router';
import { bindActionCreators } from 'redux';
import _ from 'underscore';

import { BrowserTitleService } from '~commonInfra/services/BrowserTitleService';
import { ISanitizer, sanitize } from '~commonInfra/utils/Sanitize';
import { SessionSiteSelectors } from '~selectors/SessionInfoSelectors';
import { setPreviousUrl } from '~src/app/actions/spaces/SetPreviousUrlReducerAction';
import { getAppStore } from '~src/app/AppStore';
import { AppState } from '~src/app/state/AppState';
import { MapDispatchToProps, MapStateToProps } from '~src/app/utils/ConnectUtils';
import { Messages } from '~src/codegen/Localize';
import { editDatasource } from '~src/datasource/actions/EditDatasourceAction';
import { IDataSourceDetailInfo, IDataSourceInfo } from '~src/datasource/models/IDataSourceInfo';
import { DataSources, DataSourcesResource } from '~src/datasource/resources/Datasources';
import { IDatasourceActions } from '~src/datasource/ServerApi/types/IDatasourceActions';
import { DatasourcePublishResultService } from '~src/datasources/services/DatasourcePublishResultService';
import { ExtractActionStatusService } from '~src/extract/services/ExtractActionStatusService';
import { Projects, ProjectsResource } from '~src/projects/resources/Projects';
import { RequestAccessError } from '~src/requestAccess/components/RequestAccessError';
import { ServerSettingsUnauthenticated } from '~src/settings/services/ServerSettingsUnauthenticated';
import { contentCountsUpToDate } from '~src/spacesPlatform/actions/ContentCountsActions';
import { ContentSpaceInfo, enterContentSpace } from '~src/spacesPlatform/actions/EnterSpaceAction';
import { addContentFavorite, removeContentFavorite } from '~src/spacesPlatform/actions/FavoritesActions';
import { openContentDetailDialog } from '~src/spacesPlatform/actions/OpenContentDetailDialogActions';
import { requestSpaceContents } from '~src/spacesPlatform/actions/RequestSpaceContentsActions';
import { setOrderAction } from '~src/spacesPlatform/actions/SetOrderAction';
import { updateBreadcrumbs } from '~src/spacesPlatform/actions/UpdateBreadcrumbsAction';
import {
  addContentTypeToProjectHierarchy,
  populateBreadcrumbsForPS,
  populateContentBreadcrumbs
} from '~src/spacesPlatform/components/breadcrumbs/BreadcrumbHelpers';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';
import { DatasourceSubspaceName, SpaceInfo } from '~src/spacesPlatform/state/SpaceInfo';
import { Filter } from '~src/ts/helpers/Filter';
import { IFavoritableItemInfo, INamedItemInfo, ISpaceTypeInfo } from '~src/ts/models/resourceInfoParts';
import {
  callViaApiService,
  GetLocationForContentRequestParams,
  GetLocationForContentResponse
} from '~src/ts/restApi/location/getLocationForContent';
import { IGetPreferredRefreshOptionResult } from '~src/ts/ServerApi/requests/GetPreferredRefreshOption';
import { IOrderClause } from '~src/ts/ServerApi/types/IOrderClause';
import { RefreshModeTypes } from '~src/ts/ServerApi/types/IRemoteRefreshProperties';

import {
  ConnectedQueryParamUpdateProvider,
  QueryParamUpdateProviderOwnProps
} from '../../app/spaces/RouterHelpers/QueryParamUpdateProvider';
import { RouterError } from '../../app/spaces/RouterHelpers/RouterError';
import { ConnectedDatasourceSpace } from './ConnectedDatasourceSpace';
import { DatasourceSpaceOwnProps } from './DatasourceSpace';


export interface DatasourceSpaceProviderOwnProps {
  readonly isNLPServiceEnabled: boolean;
  readonly routeLocationSearch: string;
  readonly siteName: string;
  readonly subspaceName: DatasourceSubspaceName;
  readonly datasourceId: string;
  readonly urls: SpacesSiteUrls;
  readonly displayPublishResult: boolean;
  readonly publishResultUrl: string;
  readonly isWebExtractionEnabled: boolean;
  readonly renderDatasourceSpace: (props: DatasourceSpaceOwnProps) => JSX.Element;
  readonly fetchedAllData: () => void;
  readonly spaceInfo: SpaceInfo;
}

export interface DatasourceSpaceProviderDispatchProps {
  readonly contentCountsUpToDate: () => void;
  readonly enterContentSpace: (spaceInfo: ContentSpaceInfo) => void;
  readonly requestSpaceContents: () => void;
  readonly setOrderAction: (order: ReadonlyArray<IOrderClause>) => void;
  readonly addContentFavorite: (item: IFavoritableItemInfo) => void;
  readonly removeContentFavorite: (item: IFavoritableItemInfo) => void;
  readonly setPreviousUrl: (previousUrl: string) => void;
  readonly openContentDetailDialog: (content: IContentInfo, availableActions: IDatasourceActions,  location: string) => void;
  readonly editDatasource: (content: IContentInfo, skipPrepCheck: boolean) => void;
}

export interface DatasourceSpaceProviderPropsFromServices {
  readonly dataSources: DataSourcesResource;
  readonly sanitizer: ISanitizer;
  readonly projects: ProjectsResource;
  readonly extractActionStatusService: ExtractActionStatusService;
}
export interface DatasourceSpaceProviderStateProps {
  readonly isRequestAccessEnabled: boolean;
}

export interface DatasourceSpaceProviderStateProps {
  readonly isRequestAccessEnabled: boolean;
}

export type DatasourceSpaceNonServiceProps = DatasourceSpaceProviderOwnProps & DatasourceSpaceProviderDispatchProps & DatasourceSpaceProviderStateProps;

export type DatasourceSpaceProviderContainerProps = DatasourceSpaceProviderOwnProps &  DatasourceSpaceProviderDispatchProps & DatasourceSpaceProviderPropsFromServices & DatasourceSpaceProviderStateProps;

export interface DatasourceSpaceProviderContainerState {
  readonly datasourceActions: IDatasourceActions;
  readonly datasourceInfo: IDataSourceInfo;
  readonly datasourceDetailInfo: IDataSourceDetailInfo;
  readonly initialRender: boolean;
  readonly renderChildren: boolean;
  readonly hasError: boolean;
  readonly preferredRefreshOption: IGetPreferredRefreshOptionResult;
  readonly errorCode: number;
  readonly isExtractRefreshSchedulingAllowedForDatasources: boolean;
  readonly location: string;
}

const InitialState: DatasourceSpaceProviderContainerState = {
  datasourceActions: undefined,
  datasourceInfo: undefined,
  datasourceDetailInfo: undefined,
  initialRender: true,
  renderChildren: false,
  hasError: false,
  preferredRefreshOption: undefined,
  errorCode: undefined,
  isExtractRefreshSchedulingAllowedForDatasources: true,
  location: undefined
};

export class DatasourceSpaceProviderContainer extends React.PureComponent<DatasourceSpaceProviderContainerProps, DatasourceSpaceProviderContainerState> {

  private projectHierarchy: INamedItemInfo[] = undefined;
  private unmounted: boolean = false;

  public constructor(props: DatasourceSpaceProviderContainerProps){
    super(props);
    this.state =  InitialState;
  }

  public render() {
    if (this.state.renderChildren) {
      const redirect: JSX.Element = this.getRedirects();
      if (redirect) {
        return redirect;
      }

      return this.props.renderDatasourceSpace({
        datasourceDetail: {
          usageInfo: this.state.datasourceInfo.usageInfo,
          usageInfoMaxValues: this.state.datasourceInfo.usageInfoMaxValues,
          ...this.state.datasourceDetailInfo
        },
        datasourceActions: this.state.datasourceActions,
        openDetail: this.wrappedOpenContentDetailDialog,
        addToFavorites: this.favorite,
        removeFromFavorites: this.unfavorite,
        editDatasource: this.editDatasource,
        sanitizer: this.props.sanitizer,
        showNLPSubspaces: this.state.datasourceActions.showNlp && this.props.isNLPServiceEnabled,
        bridgeEnabled: ServerSettingsUnauthenticated.isBridgeEnabled(),
        isExtractRefreshSchedulingAllowedForDatasources: this.state.isExtractRefreshSchedulingAllowedForDatasources
      });

    } else if (this.state.hasError) {
      if(this.props.isRequestAccessEnabled) {
        return <RequestAccessError url={this.props.urls.exploreUrl()} contentIdentifier={this.props.datasourceId} contentType={'datasource'} errorCode={this.state.errorCode}/>;
      }
      return <RouterError errorType={'datasources'} urls={this.props.urls} />;
    } else {
      return null;
    }
  }

  public getRedirects(): JSX.Element {
    const currSubspace = this.props.subspaceName;
    if ((currSubspace === 'nlp' || currSubspace === 'askDataUsageAnalytics') && (!this.state.datasourceActions.showNlp || !this.props.isNLPServiceEnabled)) {
      return <Redirect to={this.props.urls.datasourceConnectionsPath(this.props.datasourceId)} />;
    }

    if (currSubspace === 'extractRefreshes' && this.state.datasourceDetailInfo.refreshMode === RefreshModeTypes.Remote) {
      return <Redirect to={this.props.urls.datasourceLocalExtractRefreshesPath(this.props.datasourceId)} />;
    }

    if (currSubspace === 'remoteRefreshSchedules' && this.state.datasourceDetailInfo.refreshMode !== RefreshModeTypes.Remote) {
      /// Don't assume the refresh mode property is defined. If it isn't, 'extractTasks' is the default state.
      return <Redirect to={this.props.urls.datasourceExtractRefreshesPath(this.props.datasourceId)} />;
    }

    return null;
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  public componentDidMount() {
    this.getData();
  }

  public componentDidUpdate(prevProps: DatasourceSpaceProviderContainerProps) {
    if (this.changedDatasource(prevProps)) {
      this.projectHierarchy = undefined;
      this.updateState(InitialState /* want the initial state for the new data source */);
      this. getData();
      return;
    }

    if (this.props.displayPublishResult && _.isUndefined(this.state.preferredRefreshOption)) {
      // displayPublishResult comes in after mounting often, so do a specific check for it here
      // and fetch the info it needs
      this.getPreferredRefreshOption();
    }

    // if we dont have all we need - do nothing
    if (!this.haveAllDataNeededToRender()) {
      return;
    }

    if (this.state.initialRender) {
      this.changeSubspace(false, undefined);

      this.setState({initialRender: false, renderChildren: true}, () => {
        if (this.props.displayPublishResult) {
          DatasourcePublishResultService.handlePublishedDatasource(
            this.state.datasourceDetailInfo,
            this.state.preferredRefreshOption,
            this.props.publishResultUrl);

          this.removePublishDialogUrlParams();
        }
      });

      BrowserTitleService.setTitleWithPlaceEntityName({ placeEntityName: this.state.datasourceDetailInfo.name });

    } else if (prevProps.subspaceName !== this.props.subspaceName) {
      // if we were at the nlp subspace and the feature was disabled then we reached here due to a redirect and it's likely
      // that the contentCounts didn't get initialized and should be fetched again.
      const fetchCounts = prevProps.subspaceName === 'nlp' && !this.state.datasourceActions.showNlp;
      this.changeSubspace(!fetchCounts, prevProps.subspaceName);
    }
  }

  private getData = () => {
    this.getDatasource();
    this.getDatasourceDetail();
    this.getActions();
    this.getExtractRefreshSchedulingForDatasource();
  }

  private getExtractRefreshSchedulingForDatasource = async () => {
    try {
      const result = await this.props.dataSources.getExtractRefreshSchedulingForDatasourceRequest(this.props.datasourceId);
      this.updateState({isExtractRefreshSchedulingAllowedForDatasources: result.allowed});
    } catch (e) {}
  }

  private changedDatasource(prevProps: DatasourceSpaceProviderContainerProps) {
    return !_.isUndefined(this.props.siteName) &&
      !_.isUndefined(this.props.datasourceId) &&
      (prevProps.siteName !== this.props.siteName || prevProps.datasourceId !== this.props.datasourceId);
  }

  private haveAllDataNeededToRender() {
    return !_.isUndefined(this.state.datasourceInfo) &&
      !_.isUndefined(this.state.datasourceDetailInfo) &&
      !_.isUndefined(this.state.datasourceActions) &&
      (!_.isUndefined(this.state.location)) &&
      (!this.props.displayPublishResult || !_.isUndefined(this.state.preferredRefreshOption));
  }

  private getDatasourceDetail = async () => {
    try {
      const datasourceDetail = await this.props.dataSources.getById(this.props.datasourceId);
      this.updateState({ datasourceDetailInfo: datasourceDetail });
      let contentLocation = undefined;
      if (this.props.subspaceName === 'askDataUsageAnalytics') {
        contentLocation = 'PROJECT';
        this.updateState({location: contentLocation});
        this.populateBreadcrumbsForAskDataUsageAnalytics();
        this.props.enterContentSpace(
          {
            item: this.state.datasourceDetailInfo,
            spaceType: this.props.spaceInfo.spaceType,
            subspaceName: this.props.spaceInfo.subspaceName,
            contentLocation: 'PROJECT',
            ownerId: this.state.datasourceDetailInfo.owner.id
          }
        );
      } else {
        const requestParam: GetLocationForContentRequestParams = {
          contentType: this.state.datasourceDetailInfo.contentType,
          contentLuid: this.state.datasourceDetailInfo.luid
        };
        try{
          await callViaApiService(requestParam).then(
            async (result: GetLocationForContentResponse) => {
              if (!this.unmounted) {
                contentLocation = await result.contentLocation.type;
                this.updateState({location: contentLocation});
                this.props.enterContentSpace(
                  {
                    item: this.state.datasourceDetailInfo,
                    spaceType: this.props.spaceInfo.spaceType,
                    subspaceName: this.props.spaceInfo.subspaceName,
                    contentLocation: contentLocation,
                    ownerId: this.state.datasourceDetailInfo.owner.id
                  }
                );
                if(result.contentLocation.type === 'PERSONAL_SPACE') {
                  this.getPersonalSpaceBreadcumbs();
                }else{
                  this.getProjectHierarchyAndUpdateBreadcrumbs();
                }
              }
            }
          );
        } catch(e) {
          contentLocation = 'PROJECT';
          this.enterSpace(contentLocation);
          populateContentBreadcrumbs([this.state.datasourceDetailInfo], this.props.urls);
        }
      }

      this.updateState({location: contentLocation});
      this.props.fetchedAllData();
      if (this.props.isWebExtractionEnabled) {
        this.pollAndUpdateExtractPendingStatus();
      }
    } catch (e) {
      this.updateState({ hasError: true, errorCode: e as number });
    }
  }

  private enterSpace(contentLocation: string) {
    this.updateState({location: contentLocation});
    this.props.enterContentSpace(
      {
        item: this.props.spaceInfo.item,
        spaceType: this.props.spaceInfo.spaceType,
        subspaceName: this.props.spaceInfo.subspaceName,
        contentLocation: contentLocation,
        ownerId: this.state.datasourceDetailInfo.owner.id
      }
    );
  }

  private getDatasource = async () => {
    try {
      const datasource = await this.props.dataSources.all()
        .withFilters([Filter.fieldEq('id', this.props.datasourceId)])
        .getFirst();
      this.updateState({datasourceInfo: datasource});
    } catch (e) {
      this.updateState({hasError: true, errorCode: e as number});
    }
  }

  private getActions = async () => {
    try {
      const actions = await this.props.dataSources.actions(this.props.datasourceId);
      this.updateState({datasourceActions: actions});
    } catch (e) {}
  }

  private pollAndUpdateExtractPendingStatus = async () => {
    try {
      this.props.extractActionStatusService.pollAndUpdateExtractActionStatus(this.state.datasourceInfo);
      // if polling failed will do nothing
    } catch (e) {}
  }

  private getProjectHierarchyAndUpdateBreadcrumbs = async () => {
    let projectHierarchy: INamedItemInfo[] = await this.props.projects.getProjectHierarchy(this.state.datasourceDetailInfo.project.id);
    this.projectHierarchy = projectHierarchy;
    this.populateBreadcrumbsWithProjectHierarchy(projectHierarchy);
  }

  private getPersonalSpaceBreadcumbs = () => {
    populateBreadcrumbsForPS([this.state.datasourceDetailInfo], this.props.urls);
  }

  private populateBreadcrumbsWithProjectHierarchy = (projectHierarchy: INamedItemInfo[]) => {
    const projectHierarchyWithType = addContentTypeToProjectHierarchy(projectHierarchy);
    populateContentBreadcrumbs([...projectHierarchyWithType, this.state.datasourceDetailInfo], this.props.urls);
  }

  private populateBreadcrumbsForAskDataUsageAnalytics = () => {
    getAppStore().dispatch(updateBreadcrumbs([
      {
        text: this.state.datasourceDetailInfo.name,
        href: this.props.urls.datasourceUrl(this.state.datasourceDetailInfo.id),
      }, {
        text: Messages.datasourceAskDataMetrics(),
      }
    ]));
  }

  private updateState<K extends keyof DatasourceSpaceProviderContainerState>(newState: Pick<DatasourceSpaceProviderContainerState, K>): void {
    if (!this.unmounted) {
      this.setState(newState);
    }
  }

  private changeSubspace = (shouldNotFetchCounts: boolean, prevSubspace: DatasourceSubspaceName) => {
    let subspaceName = this.props.subspaceName;

    this.props.enterContentSpace(
      {
        item: this.state.datasourceDetailInfo,
        spaceType: 'datasource',
        subspaceName: subspaceName,
        contentLocation: this.state.location,
        ownerId: this.state.datasourceDetailInfo.owner.id
      }
    );

    // If we just switched tabs we know content counts are up to date.
    // They will get set to false by the above EnterSpaceAction.
    if(shouldNotFetchCounts) {
      this.props.contentCountsUpToDate();
    }

    if (subspaceName === 'askDataUsageAnalytics') {
      this.populateBreadcrumbsForAskDataUsageAnalytics();

    } else if (prevSubspace === 'askDataUsageAnalytics') {
      if (_.isUndefined(this.projectHierarchy)) {
        // if the previous subspace was askDataUsageAnalytics, then we need to reset the breadcrumbs and fetch project hierarchy.
        // Otherwise, the breadcrumbs are already correct
        // don't need to await on this function because it does not affect requestSpaceContents
        this.getProjectHierarchyAndUpdateBreadcrumbs();
      } else {
        this.populateBreadcrumbsWithProjectHierarchy(this.projectHierarchy);
      }
    }

    this.props.requestSpaceContents();
  }

  private wrappedOpenContentDetailDialog = () => {
    const { datasourceActions, datasourceDetailInfo, datasourceInfo, location } = this.state;

    const datasourceWithUsageInfo = {
      usageInfo: datasourceInfo.usageInfo,
      usageInfoMaxValues: datasourceInfo.usageInfoMaxValues,
      ...datasourceDetailInfo
    };

    this.props.openContentDetailDialog(datasourceWithUsageInfo, datasourceActions, location);
  }

  private favorite = () => {
    this.props.addContentFavorite(this.state.datasourceDetailInfo);
  }

  private unfavorite = () => {
    this.props.removeContentFavorite(this.state.datasourceDetailInfo);
  }

  private editDatasource = () => {
    this.props.editDatasource(this.state.datasourceDetailInfo, true /* skipPrepCheck */);
  }

  private getPreferredRefreshOption = async () => {
    if (this.props.displayPublishResult) {
      try {
        const preferredRefreshOption: IGetPreferredRefreshOptionResult = await this.props.dataSources.getPreferredRefreshOption(this.props.datasourceId);
        this.updateState({ preferredRefreshOption: preferredRefreshOption });
      } catch (e) {}
    }
  }

    /**
   * Prevents the publish dialog from showing up multiple times because of state.hardReload
   * re-rendering this react component and the previousUrl being the same in the app state.
   */
  private removePublishDialogUrlParams = () => {
    const cleanDSUrls = window.location.href;
    this.props.setPreviousUrl(cleanDSUrls);
  }
}

const DatasourceSpaceProviderServices: React.ComponentType<DatasourceSpaceNonServiceProps> = function DatasourceSpaceProviderServices(props: DatasourceSpaceNonServiceProps) {
  return (
    <DatasourceSpaceProviderContainer
      {...props}
      dataSources={DataSources}
      sanitizer={sanitize}
      projects={Projects}
      extractActionStatusService={ExtractActionStatusService}
    />
  );
};

const mapStateToProps: MapStateToProps<DatasourceSpaceProviderStateProps> = (state: AppState) => ({
  isRequestAccessEnabled: SessionSiteSelectors.isRequestAccessEnabled(state)
});

const mapDispatchToProps: MapDispatchToProps<DatasourceSpaceProviderDispatchProps> = (dispatch) => bindActionCreators({
  contentCountsUpToDate,
  requestSpaceContents,
  enterContentSpace,
  addContentFavorite,
  removeContentFavorite,
  setOrderAction,
  setPreviousUrl,
  openContentDetailDialog,
  editDatasource,
}, dispatch);

export const ConnectedDatasourceSpaceProviderContainer: React.ComponentClass<DatasourceSpaceProviderOwnProps>
  = connect(mapStateToProps, mapDispatchToProps)(DatasourceSpaceProviderServices);

const renderDatasourceSpace = (props: DatasourceSpaceOwnProps): JSX.Element => <ConnectedDatasourceSpace {...props} />;

export interface LocationPathProp {
  readonly routeLocationPath: string;
}
export type DatasourceSpaceProviderProps = LocationPathProp & Omit<DatasourceSpaceProviderOwnProps, 'renderDatasourceSpace' | 'fetchedAllData' | 'spaceInfo'>;

export interface DatasourceSpaceProviderState {
  readonly fetchedAllData: boolean;
}

export class DatasourceSpaceProvider extends React.PureComponent<DatasourceSpaceProviderProps, DatasourceSpaceProviderState> {
  public constructor(props: DatasourceSpaceProviderProps) {
    super(props);
    this.state = {fetchedAllData: false};
  }

  public render() {
    const queryUpdaterProps: QueryParamUpdateProviderOwnProps = {
      routeLocationPath:this.props.routeLocationPath,
      routeLocationSearch: this.props.routeLocationSearch,
      shouldUpdateContentTypes: true,
      shouldUpdateFilters: false,
      shouldUpdateSort: true,
      shouldUpdateBreadcrumbs: this.state.fetchedAllData
    };

    const MyItem: ISpaceTypeInfo = {
      id: this.props.datasourceId,
      contentType: 'datasource'
    };

    const DatasourceSpaceInfo: SpaceInfo = {
      item: MyItem,
      spaceType: 'datasource',
      subspaceName: this.props.subspaceName,
    };

    return (
      <ConnectedQueryParamUpdateProvider {...queryUpdaterProps}>
        <ConnectedDatasourceSpaceProviderContainer {...{...this.props, renderDatasourceSpace, fetchedAllData: this.fetchedAllData, spaceInfo: DatasourceSpaceInfo}} />
      </ConnectedQueryParamUpdateProvider>
    );
  }

  private fetchedAllData = () => {
    this.setState({fetchedAllData: true});
  }
}
